/*
 * VatomInc. Copyright (c) 2022, all rights reserved.
 *
 * This source code is licensed under the license found in the LICENSE file in the root directory of this source tree.
 */
// @ts-ignore
// import AnimationManager from "@vatom/3d-face/src/AnimationManager";
import { ResourceSnapshot, TokenType } from '@vatom/sdk/core'
import { Texture } from 'three'
// @ts-ignore
import GifLoader from 'three-gif-loader'

import { AnimationManager } from './AnimationManager'
import ModelLoader from './ModelLoader'
import { ARConfig } from './TokenPlugin'

declare let THREE: any

//
// Represents a Token in 3D

export default class TokenObject {
  token: TokenType
  scene: any
  renderer: any
  audioListener: any
  onClick: any
  tokenActionPerformedListener: any
  // ResizeObjects: boolean;
  container: any
  loadingIcon: any
  animation: any
  animatedGif: any
  loaderStep: number
  hasSetPosition?: boolean
  randomHeight?: number
  appliedDistance?: number
  lastX?: number
  lastY?: number
  lastZ?: number
  currentTween?: any
  currentTweenTime?: number
  resource?: ResourceSnapshot
  config: ARConfig

  constructor(token: TokenType, scene: any, renderer: any, audioListener: any, config: ARConfig) {
    // Store token object
    this.token = token

    // Store scene root
    this.scene = scene

    // Store renderer
    this.renderer = renderer

    // Store audio listener
    this.audioListener = audioListener

    this.config = config

    // Set by the parent to receive on click events
    this.onClick = null

    // Set by the parent to receive token action events
    this.tokenActionPerformedListener = null

    // Should we resize all objects to fit into a 1x1x1 meter box?
    // this.ResizeObjects = config.resizeObjects !== undefined ? config.resizeObjects : true;

    // Create 3D object
    this.container = new THREE.Object3D()
    this.container.onClick = this.handleClick.bind(this)

    // Create loading animation
    this.loadingIcon = this.createLoader()
    this.container.add(this.loadingIcon)

    this.loaderStep = 0
  }

  /** @returns Promise */
  async load() {
    // Catch errors
    let error: any = null
    try {
      // Load 3D model
      await this.load3D()
    } catch (err) {
      error = err

      try {
        await this.load2D()
        error = null
      } catch (e) {
        error = err
      }
    }

    // Remove loader
    this.container.remove(this.loadingIcon)

    // Throw error if there was an error
    if (error) throw error
  }

  async load3D() {
    // const view = this.token.getView(this.token.faceSelector.Engaged);
    const resource = this.token.resources.find(r => r.type === '3d/scene')

    // If still no resource, stop
    if (!resource?.url) throw new Error('No scene resource found')
    this.resource = resource
    console.info('Rendering 3D object: ', resource)
    // Load model
    const obj = await ModelLoader.load(resource.url)

    obj.scale.set(1, 1, 1)
    // Attach scene to view
    this.container.add(obj)

    // Check if we should resize
    if (this.config.resizeObjects) {
      // Get bounding box of entire scene
      const box = new THREE.Box3()
      const vec = new THREE.Vector3()
      box.setFromObject(this.container)
      box.getSize(vec)
      const sceneRadius = vec.length()
      // const sceneRadius = box.getSize().length();
      console.info('Scene Radius: ', sceneRadius)

      // Scale the scene down
      const desiredRadius = this.config.desiredRadius || 1
      const scale = desiredRadius / sceneRadius
      this.container.scale.setScalar(scale)
    }

    // Apply custom transforms to the object
    if (resource.ar_transform?.scale)
      obj.scale.set(
        resource.ar_transform.scale.x || 1,
        resource.ar_transform.scale.y || 1,
        resource.ar_transform.scale.z || 1
      )
    if (resource.ar_transform?.translate)
      obj.position.set(
        resource.ar_transform.translate.x || 0,
        resource.ar_transform.translate.y || 0,
        resource.ar_transform.translate.z || 0
      )
    if (resource.ar_transform?.rotate)
      obj.rotation.set(
        resource.ar_transform.rotate.x || 0,
        resource.ar_transform.rotate.y || 0,
        resource.ar_transform.rotate.z || 0
      )

    // Create animation manager if needed
    if (obj.animations) {
      // Get animation rules TODO?
      // let animRules = resource?.animation_rules || this.token.properties.animation_rules || []

      const animRules = resource?.animation_rules || []
      // Create animation manager
      this.animation = new AnimationManager(
        obj,
        obj.animations,
        animRules,
        (this.token as any).payload,
        this.audioListener
      )
      // this.animation.requestingResourceURL = (name: string) => BLOCKv.UserManager.encodeAssetProvider(this.token.properties.resources.find(r => r.name === name).value.value)
      this.animation.requestingResourceURL = (name: string) => {
        const resource = this.token.resources.find(r => r.name === name)
        if (resource) return resource.url
        else return undefined
      }
      this.animation.requestingPerformAction = async (actionDetails: any) => {
        // Catch errors
        try {
          // Perform action
          await this.token.performAction(actionDetails.name, actionDetails.payload)

          // Log analytics
          // Analytics.event(
          // 	"performAction",
          // 	{
          // 		event: "performAction",
          // 		actionUri: actionDetails.name,
          // 		eventValue: 1,
          // 	},
          // 	this.token
          // );

          // Notify plugins
          if (this.tokenActionPerformedListener)
            this.tokenActionPerformedListener({
              token: (this.token as any).payload,
              actionName: actionDetails.name,
              actionData: actionDetails.payload
            })
        } catch (err) {
          // Show error to the user, and pass it on up the chain
          // Errors.show(err);
          // throw err;
        }
      }
    }
  }

  /** Load a 2D representation of this Token */
  async load2D() {
    // Try the engaged view first
    // let view = this.token.getView(this.token.faceSelector.Engaged);
    let resource = this.token.resources.find(r => r.type.includes('image'))
    if (!resource?.url) {
      // Try the icon view
      // view = this.token.getView(this.token.faceSelector.Icon);
      resource = this.token.resources.find(r => r.type.includes('image'))
    }

    if (!resource?.url)
      throw new Error('No 3D face, and no ActivatedImage resource found for this NFT.')

    // If a gif, use GifLoader to provide animations
    if (resource.url.toLowerCase().includes('gif')) {
      const loader = new GifLoader()
      const texture = loader.load(resource.url)

      const geometry = new THREE.PlaneBufferGeometry(3, 3)
      const material = new THREE.MeshBasicMaterial({
        map: texture,
        transparent: true
      })

      this.animatedGif = new THREE.Mesh(geometry, material)
      this.container.add(this.animatedGif)

      // Aspect ratio resize
      this.animatedGif.scale.y = texture.image.height / texture.image.width
    } else {
      // Create plane
      const texture: Texture = await new Promise((resolve, reject) =>
        new THREE.TextureLoader().load(resource!.url!, resolve, undefined, reject)
      )
      const material = new THREE.SpriteMaterial({ map: texture })
      const mesh = new THREE.Sprite(material)
      this.container.add(mesh)

      // Aspect ratio resize
      mesh.scale.y = texture.image.height / texture.image.width
    }
  }

  /** Called every frame */
  onRender3D(delta: any, scene: any, camera: any) {
    if (this.animatedGif) {
      // Keep the animated GIF facing the viewport
      this.animatedGif.quaternion.copy(camera.quaternion)
    }

    if (this.animation) {
      // Update animation
      this.animation.update(delta)

      // Rotate slightly if no animations on the token
      if (
        (!this.animation.clips || this.animation.clips.length === 0) &&
        (this.resource === undefined || this.resource?.auto_rotate)
      )
        this.container.rotation.y += 1 * delta
    }
  }

  /** Create 3D loading animation */
  createLoader() {
    // Create container
    const holder = new THREE.Object3D()

    // Create loading ball 1
    let geometry = new THREE.SphereGeometry(1, 32, 32)
    let material = new THREE.MeshBasicMaterial({ color: 0x4286f4 })
    const ball1 = new THREE.Mesh(geometry, material)
    holder.add(ball1)

    // Create loading ball 2
    geometry = new THREE.SphereGeometry(1, 32, 32)
    material = new THREE.MeshBasicMaterial({ color: 0x247a26 })
    const ball2 = new THREE.Mesh(geometry, material)
    holder.add(ball2)

    // Animate loading icon
    this.loaderStep = 0
    ball1.onBeforeRender = (e: any) => {
      // Increase loader step
      this.loaderStep += 0.1

      // Resize the balls
      ball1.scale.setScalar(Math.sin(this.loaderStep) * 0.5 + 0.5)
      ball2.scale.setScalar(Math.sin(this.loaderStep + Math.PI) * 0.5 + 0.5)
    }

    // Done
    return holder
  }

  /** Called when the user clicks on this token */
  handleClick() {
    // Handle event via animation manager
    const handled = this.animation && this.animation.onClick()
    if (handled) return

    // No animation rule for "click", pass on the event
    this.onClick()
  }
}
